import React, { useState } from 'react';
import { Tag, Button, message, Form, InputNumber, Select, Input } from 'antd';
import { Add } from '@carbon/icons-react';
import Card, { CardDelete, CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import providerService from 'services/providers.service';
import { ENDASH } from 'constants';
import AsyncSelect from 'components/Select/AsyncSelect';

const getDiscountRate = ({ rate, unit }) => {
  return unit === 'RATE' ? `${rate}%` : `$${rate}`;
};

const Discounts = ({ application, isEditable = false }) => {
  const [localDiscounts, setLocalDiscounts] = useState(application?.discounts || []);
  const [deletedDiscounts, setDeletedDiscounts] = useState([]);
  const [form] = Form.useForm();

  const { isEditing, editProps } = useEditCard({
    id: 'discounts',
    onEditStart: () => {
      const formValues = {};
      localDiscounts.forEach((discount, idx) => {
        formValues[`type_${idx}`] = discount.type?.id;
        formValues[`rate_${idx}`] = discount.rate;
        formValues[`unit_${idx}`] = discount.unit;
        formValues[`additionalComments_${idx}`] = discount.additionalComments;
      });
      form.setFieldsValue(formValues);
    },
    onSave: async () => {
      await form.validateFields();
      const values = form.getFieldsValue();
      const deletePromises = deletedDiscounts.map(async (discount) => {
        if (discount.id) return providerService.deleteApplicationDiscounts(application.id, discount.id);
      });
      const updatePromises = localDiscounts.map(async (discount, idx) => {
        const payload = {
          ...(discount.id ? { id: discount.id } : {}),
          type: typeof values[`type_${idx}`] === 'number' ? { id: values[`type_${idx}`] } : values[`type_${idx}`],
          rate: values[`rate_${idx}`],
          unit: values[`unit_${idx}`],
          additionalComments: values[`additionalComments_${idx}`],
          discountOffered: true,
          application: { id: application.id },
        };
        return providerService.addUpdateApplicationDiscounts(application.id, payload);
      });
      await Promise.all(deletePromises);
      const updatedDiscounts = await Promise.all(updatePromises);
      setLocalDiscounts(updatedDiscounts);
      setDeletedDiscounts([]);
      form.resetFields();
      message.success('Discounts updated');
    },
    onDiscard: () => {
      const savedDiscounts = localDiscounts.filter((disc) => disc.id !== null);
      setLocalDiscounts([...savedDiscounts, ...deletedDiscounts]);
      setDeletedDiscounts([]);
      form.resetFields();
    },
  });

  const handleDeleteDiscount = (discountIndex) => {
    const discount = localDiscounts[discountIndex];

    if (discount.id) {
      setDeletedDiscounts([...deletedDiscounts, discount]);
    }

    const updatedDiscounts = localDiscounts.filter((_, idx) => idx !== discountIndex);
    setLocalDiscounts(updatedDiscounts);
  };

  const handleAddDiscount = () => {
    const newDiscount = {
      id: null,
      type: { id: 87, title: '' },
      rate: 0,
      unit: 'RATE',
      additionalComments: '',
      discountOffered: true,
    };

    const updatedDiscounts = [...localDiscounts, newDiscount];
    setLocalDiscounts(updatedDiscounts);

    const idx = updatedDiscounts.length - 1;
    form.setFieldsValue({
      [`type_${idx}`]: 87,
      [`rate_${idx}`]: 0,
      [`unit_${idx}`]: 'RATE',
      [`additionalComments_${idx}`]: '',
    });
  };

  return (
    <Card
      title={
        <div className="flex items-center gap-3">
          <span>Discounts</span>
          <Tag>{localDiscounts.length}</Tag>
        </div>
      }
      extra={isEditable ? <CardEdit {...editProps} /> : null}
      stickyHeader
    >
      <Form form={form} disabled={editProps.loading}>
        {localDiscounts.length > 0 &&
          localDiscounts.map((discount, idx) => (
            <Card
              key={discount?.id || `new-${idx}`}
              className="mb-4"
              collapsible
              title={discount?.type?.title || ENDASH}
              subtitle={getDiscountRate(discount)}
              extra={isEditing ? <CardDelete onConfirm={() => handleDeleteDiscount(idx)} /> : null}
              noBodyPadding
            >
              <TabularList
                indented
                data={[
                  {
                    title: 'Type',
                    value: isEditing ? (
                      <Form.Item
                        name={`type_${idx}`}
                        className="mb-0"
                        rules={[{ required: true, message: 'Type is required' }]}
                      >
                        <AsyncSelect
                          optionsApiUrl="/discount-types"
                          getOptionLabel={(option) => option.title}
                          getOptionValue={(option) => option.id}
                          isOptionSelected={(option, [value]) => option?.id === (value?.id || value)}
                        />
                      </Form.Item>
                    ) : (
                      discount?.type?.title || ENDASH
                    ),
                    titleRequired: true,
                  },
                  {
                    title: 'Amount',
                    value: isEditing ? (
                      <Form.Item
                        name={`rate_${idx}`}
                        className="mb-0"
                        rules={[{ required: true, message: 'Amount is required' }]}
                      >
                        <InputNumber min={0} className="max-w-72" />
                      </Form.Item>
                    ) : (
                      discount?.rate || ENDASH
                    ),
                    titleRequired: true,
                  },
                  {
                    title: 'Unit',
                    value: isEditing ? (
                      <Form.Item
                        name={`unit_${idx}`}
                        className="mb-0"
                        rules={[{ required: true, message: 'Unit is required' }]}
                      >
                        <Select className="max-w-72">
                          <Select.Option value="AMOUNT">$ - Dollars</Select.Option>
                          <Select.Option value="RATE">% - Percentage</Select.Option>
                        </Select>
                      </Form.Item>
                    ) : discount?.unit === 'RATE' ? (
                      '%'
                    ) : (
                      '$'
                    ),
                    titleRequired: true,
                  },
                  {
                    title: 'Additional comments',
                    value: isEditing ? (
                      <Form.Item name={`additionalComments_${idx}`} className="mb-0">
                        <Input.TextArea rows={3} className="max-w-2xl" />
                      </Form.Item>
                    ) : (
                      discount?.additionalComments
                    ),
                  },
                ]}
              />
            </Card>
          ))}
      </Form>

      {isEditing && isEditable && (
        <Button
          color="default"
          variant="dashed"
          onClick={handleAddDiscount}
          icon={<Add size={20} />}
          block
          className="mt-4"
          disabled={editProps.loading}
        >
          Add discount
        </Button>
      )}
    </Card>
  );
};

export default Discounts;
